import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MenuItem, MessageService, TreeNode } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';
import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { isEmpty, isNotEmpty, isStringEqual } from '../../../common/functions';
import { successMsg } from '../../../common/messages';
import { aggregateValidation } from '../../../common/validation';
import { notifyAndTrackError } from '../../../common/error';

import { CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';
import { AppInsightsService } from '../../../services/app-insights.service';
import {
  DictionaryCacheService,
  IAddress,
  IDictionaryEntry,
  ILocation,
  ILocationAddRequest,
  ILocationUpdateRequest,
  LocationAdminService
} from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-location-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    ProcessingComponent,
    UnsavedChangesDialogComponent
  ],

  templateUrl: 'locations-admin.component.html'
})
export class LocationAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  nodes: TreeNode[] = [];
  selectedNode!: TreeNode;

  locations: ILocation[] = [];

  containmentTypes: IDictionaryEntry[] = [];
  locationTypes: IDictionaryEntry[] = [];

  countries: IDictionaryEntry[] = [];
  states: IDictionaryEntry[] = [];
  statesForDisplay: IDictionaryEntry[] = [];

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  dialogVars: any = {
    visible: false,
    data: <ILocation>{},
    streets: ``,
    parent: [],
    title: `New Location`
  };
  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: `Admin:Locations`,
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Location Management' }]
  };

  constructor(
    private apiService: LocationAdminService,
    private dictionaryCache: DictionaryCacheService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Locations`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/locations`);
    this.dialogVars.data = <ILocation>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.locations = [];
    this.containmentTypes = [];
    this.locationTypes = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      locations: this.apiService.getLocationsForHierarchy(),
      locationTypes: this.dictionaryCache.get('locationtype'),
      containmentTypes: this.dictionaryCache.get('containmenttype'),
      states: this.dictionaryCache.get('state'),
      countries: this.dictionaryCache.get('country')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.pageVars.currentUser = response.profile.userPrincipalName;
        this.containmentTypes = response.containmentTypes;
        this.locationTypes = response.locationTypes;
        this.states = response.states;
        this.countries = response.countries;

        this.nodes = response.locations.map((x) => this.buildNode(x));
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Init:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  buildNode(location: ILocation): TreeNode {
    location.locationTypeId = location.locationTypeId.toUpperCase();
    location.containmentTypeId = location.containmentTypeId?.toUpperCase();
    if (isNotEmpty(location.address)) {
      location.address!.privateUserId = location.address!.privateUserId?.toUpperCase();
    }

    return <TreeNode>{
      key: location.id,
      label: location.name,
      data: location,
      children: location.children.map((x) => this.buildNode(x))
    };
  }

  findNode(nodes: TreeNode[] | undefined, key: string): TreeNode | undefined {
    if (isEmpty(nodes)) return undefined;
    if (isEmpty(key)) return undefined;

    let childNode = (<TreeNode[]>nodes).find((x) => x.key === key);
    if (isEmpty(childNode)) {
      for (let idx: number = 0; idx < (nodes?.length ?? 0); idx++) {
        childNode = this.findNode(nodes![idx].children, key);
        if (isNotEmpty(childNode)) return childNode;
      }
    }
    return childNode;
  }

  findNodeByName(nodes: TreeNode[] | undefined, name: string, key?: string): TreeNode | undefined {
    if (isEmpty(nodes)) return undefined;
    if (isEmpty(name)) return undefined;

    let childNode = (<TreeNode[]>nodes).find((x) => isStringEqual(x.data.name, name) && (isEmpty(key) || !isStringEqual(<string>x.key, <string>key)));

    if (isEmpty(childNode)) {
      for (let idx: number = 0; idx < (nodes?.length ?? 0); idx++) {
        childNode = this.findNodeByName(nodes![idx].children, name, key);
        if (isNotEmpty(childNode)) return childNode;
      }
    }
    return childNode;
  }

  gridNewChild(parent: ILocation) {
    this.dialogVars.data = {
      id: 'NEW',
      name: null,
      parentId: parent.id,
      locationTypeId: null,
      containmentTypeId: null,
      isSelectable: true,
      isActive: true,
      children: [],
      address: {
        id: 'NEW',
        isPrivate: false,
        privateUserId: undefined,
        streets: [],
        city: undefined,
        stateCode: undefined,
        postalCode: undefined,
        countryCode: 'US',
        isActive: true,
        action: 'INS',
        actionBy: this.pageVars.currentUser,
        actionOn: new Date(Date.now())
      },
      action: 'INS',
      actionBy: this.pageVars.currentUser,
      actionOn: new Date(Date.now())
    };
    this.dialogVars.parent = this.findNode(this.nodes, parent.id);
    this.dialogVars.streets = ``;
    this.dialogVars.visible = true;
  }

  gridNewRow() {
    this.dialogVars.data = {
      id: 'NEW',
      name: null,
      parentId: null,
      locationTypeId: null,
      containmentTypeId: null,
      isSelectable: true,
      isActive: true,
      children: [],
      address: {
        id: 'NEW',
        isPrivate: false,
        privateUserId: undefined,
        streets: [],
        city: undefined,
        stateCode: undefined,
        postalCode: undefined,
        countryCode: 'US',
        isActive: true,
        action: 'INS',
        actionBy: this.pageVars.currentUser,
        actionOn: new Date(Date.now())
      },
      action: 'INS',
      actionBy: this.pageVars.currentUser,
      actionOn: new Date(Date.now())
    };
    this.dialogVars.parent = null;
    this.dialogVars.streets = ``;
    this.dialogVars.visible = true;
    this.dialogVars.title = `New Location`;
  }

  gridRowEditCancel() {
    this.dialogVars.data = <ILocation>{};
    this.dialogVars.visible = false;
    this.pageVars.isEditing = false;
  }

  gridRowEditInit(c: ILocation) {
    this.pageVars.isEditing = true;

    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());

    if (isEmpty(c.address)) {
      c.address = {
        id: 'NEW',
        isPrivate: false,
        privateUserId: undefined,
        streets: [],
        city: undefined,
        stateCode: undefined,
        postalCode: undefined,
        countryCode: 'US',
        isActive: true,
        action: 'INS',
        actionBy: this.pageVars.currentUser,
        actionOn: c.actionOn
      };
    }

    this.dialogVars.streets = null;
    if (isNotEmpty(c.address!.streets)) {
      this.dialogVars.streets = Object.keys(c.address!.streets)
        .sort((a, b) => +a - +b)
        .map((k) => c.address!.streets[+k])
        .join('\n');
    }
    this.dialogVars.parent = this.findNode(this.nodes, c.parentId);
    this.dialogVars.data = c;
    this.dialogVars.title = `Modify '${c.name}'`;
    this.dialogVars.visible = true;
  }

  gridRowEditSave() {
    this.pageVars.isEditing = true;
    this.pageVars.isReady = false;

    // reconcile streets
    if (isNotEmpty(this.dialogVars.streets)) {
      this.dialogVars.data.address.streets = this.dialogVars.streets.split(/\r?\n/).reduce(
        (acc: any, val: string, idx: number) => {
          acc[idx + 1] = val;
          return acc;
        },
        {} as Record<number, string>
      );
    } else {
      this.dialogVars.data.address.streets = {};
    }

    // reconcile parent
    const oldParent = this.findNode(this.nodes, this.dialogVars.data.parentId);
    if (oldParent) {
      oldParent.children = oldParent?.children?.filter((x) => x.key !== this.dialogVars.data.id);
    } else {
      this.nodes = this.nodes.filter((x) => x.key !== this.dialogVars.data.id).sort((a, b) => (a.data.name > b.data.name ? 1 : -1));
    }
    this.dialogVars.data.parentId = this.dialogVars.parent?.key;

    const parent = this.findNode(this.nodes, this.dialogVars.parent?.key);
    if (parent) {
      parent?.children?.push(this.buildNode(this.dialogVars.data));
      parent.children = parent?.children?.sort((a, b) => (a.data.name > b.data.name ? 1 : -1));
    } else {
      this.nodes.push(this.buildNode(this.dialogVars.data));
      this.nodes = this.nodes.sort((a, b) => (a.data.name > b.data.name ? 1 : -1));
    }

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Save`, { Location: this.dialogVars.data });
    const action =
      this.dialogVars.data.action === 'INS'
        ? this.apiService.addLocation(<ILocationAddRequest>this.buildRequest(this.dialogVars.data))
        : this.apiService.updateLocation(<ILocationUpdateRequest>this.buildRequest(this.dialogVars.data));

    firstValueFrom(action)
      .then((result: any) => {
        if (result) {
          this.dialogVars.data.id = result;
        }

        this.dialogVars.data.locationType = this.locationTypes.find((lt: any) => lt.id === this.dialogVars.data.locationTypeId);

        this.dialogVars.data.containmentType = this.containmentTypes.find((ct: any) => ct.id === this.dialogVars.data.containmentTypeId);

        // Re-fetch the data set
        firstValueFrom(this.apiService.getLocationsForHierarchy())
          .then((locations) => {
            this.nodes = locations.map((x) => this.buildNode(x));
            this.changeDetector.detectChanges();
          })
          .catch((err) => {
            notifyAndTrackError(this.msgService, this.appInsights, err, {
              component: this.pageVars.pageId,
              action: 'HierarchyReload:Error'
            });
          });

        this.changeDetector.detectChanges();
        successMsg(this.msgService, 'Location has been saved');

        this.dialogVars.visible = false;
        this.dialogVars.streets = [];
        this.dialogVars.parent = null;
        this.dialogVars.data = <ILocation>{};
      })
      .catch((err) => {
        if ((<string>err.message).includes('Validation') && err.failures?.length) {
          const rules = err.failures.map((f: any) => ({ valid: false, message: `${f.field}: ${f.message}` }));
          aggregateValidation(this.msgService, rules, { dedupe: true });
        } else {
          notifyAndTrackError(this.msgService, this.appInsights, err, {
            component: this.pageVars.pageId,
            action: 'Grid:Save:Error'
          });
        }
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  dialogIsValid(): boolean {
    if (isEmpty(this.dialogVars.data.name)) return false;
    if (isEmpty(this.dialogVars.data.containmentTypeId)) return false;
    if (isEmpty(this.dialogVars.data.locationTypeId)) return false;

    const parent = this.findNode(this.nodes, this.dialogVars.parent?.key);
    const duplicateName = parent?.children?.find((x) => x.key !== this.dialogVars.data.id && isStringEqual(x.data.name, this.dialogVars.data.name));
    if (isNotEmpty(duplicateName)) return false;

    return true;
  }

  dialogStateList(): IDictionaryEntry[] {
    if (isEmpty(this.dialogVars.data.address.countryCode)) {
      return [];
    }
    return this.states.filter((x) => x.id.startsWith(<string>this.dialogVars.data.address.countryCode));
  }

  buildRequest(location: ILocation): ILocationAddRequest | ILocationUpdateRequest {
    const address = <IAddress>{
      streets: location.address?.streets,
      city: location.address?.city,
      stateCode: location.address?.stateCode,
      postalCode: location.address?.postalCode,
      countryCode: location.address?.countryCode,
      isPrivate: location.address?.isPrivate,
      privateUserId: location.address?.privateUserId,
      isActive: location.address?.isActive,
      action: location.address?.action,
      actionBy: location.address?.actionBy,
      actionOn: location.address?.actionOn
    };
    if (location.address?.action === 'UPD') address['id'] = location.address?.id;

    const request: ILocationAddRequest | ILocationUpdateRequest = {
      name: location.name,
      parentId: location.parentId,
      locationTypeId: location.locationTypeId,
      containmentTypeId: location.containmentTypeId,
      isActive: location.isActive,
      isSelectable: location.isSelectable,
      action: location.action,
      actionBy: location.actionBy,
      actionOn: location.actionOn
    };

    if (isNotEmpty(location.address?.streets) && isNotEmpty(location.address?.city) && isNotEmpty(location.address?.postalCode)) {
      request['address'] = address;
    }

    if (request.action === 'INS') {
      return <ILocationAddRequest>request;
    }

    (<ILocationUpdateRequest>request).id = location.id;
    return <ILocationUpdateRequest>request;
  }

  arrayToObject(arr: any[]): Record<number, string> {
    return arr.reduce(
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<number, string>
    );
  }

  locationTypesList() {
    const siteLocationType = this.locationTypes.find((x) => isStringEqual(x.text, 'Site'));

    if (isStringEqual(this.dialogVars.data.id, 'NEW'))
      return isEmpty(this.dialogVars.data.parentId) ? this.locationTypes : this.locationTypes.filter((x) => !isStringEqual(x.text, 'Site'));

    return this.dialogVars.data.locationTypeId === siteLocationType?.id
      ? this.locationTypes
      : this.locationTypes.filter((x) => !isStringEqual(x.text, 'Site'));
  }

  locationTypesDisabled() {
    if (isStringEqual(this.dialogVars.data.id, 'NEW')) return false;
    const siteLocationType = this.locationTypes.find((x) => isStringEqual(x.text, 'Site'));
    return this.dialogVars.data.locationTypeId === siteLocationType?.id;
  }

  navigateToCatalog(locationId: any) {
    this.router.navigateByUrl(`admin/sitecatalog/${locationId}`);
  }
}
